import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useParticipants,
  useTrackToggle,
  useLocalParticipant,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Mic, MicOff, LogOut, Loader2, Sparkles } from "lucide-react";
import LiveTranscription from "@/components/LiveTranscription";
import useLiveCaptions from "@/hooks/useLiveCaptions";

function Lobby({ meetingId, joinInfo, onJoined }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!consent) {
      toast.error("Please consent to be recorded to continue.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/join-public`, {
        name: name.trim(),
        email: email.trim(),
        consent: true,
      });
      onJoined(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen grid place-items-center p-6"
      style={{ background: "linear-gradient(180deg, hsl(var(--tone-lavender)) 0%, hsl(var(--background)) 100%)" }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="yl-orb yl-orb-a opacity-50" />
        <div className="yl-orb yl-orb-c opacity-40" />
      </div>
      <div data-testid="lobby-card" className="relative yl-glass rounded-3xl p-8 max-w-md w-full shadow-lift">
        <div className="eyebrow text-primary-hover">Join meeting</div>
        <h1 className="font-heading text-4xl leading-tight tracking-tight mt-2">
          {joinInfo?.title || "Yanthra MeetIQ"}
        </h1>
        <div className="mt-2 text-sm text-muted-foreground">
          {joinInfo?.organizer_name && (<>Hosted by <strong>{joinInfo.organizer_name}</strong> · </>)}
          {joinInfo?.meeting_date} {joinInfo?.start_time && `· ${joinInfo.start_time}`}
        </div>
        {joinInfo?.agenda?.length > 0 && (
          <ul className="mt-4 pl-0 space-y-1 text-sm">
            {joinInfo.agenda.slice(0, 5).map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-foreground/80">
                <span className="text-primary font-bold">{String(i + 1).padStart(2, "0")}</span>
                <span>{a}</span>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={submit} data-testid="meeting-join-form" className="mt-7 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="eyebrow">Your name</Label>
            <Input
              id="name" data-testid="lobby-name-input"
              value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="Priya Iyer"
              className="rounded-2xl h-11 bg-card border-border focus:border-primary"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="eyebrow">Work email</Label>
            <Input
              id="email" data-testid="lobby-email-input" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              placeholder="you@company.com"
              className="rounded-2xl h-11 bg-card border-border focus:border-primary"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <Checkbox
              data-testid="lobby-consent-checkbox"
              checked={consent} onCheckedChange={(v) => setConsent(!!v)}
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              I consent to my audio being recorded and transcribed for the
              meeting minutes. My voice will be attributed to my name.
            </span>
          </label>
          <Button
            data-testid="lobby-join-btn" type="submit" disabled={busy || !consent}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))] shadow-lift"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <span className="inline-flex items-center gap-2 font-semibold tracking-wide">
                <Sparkles className="w-4 h-4" /> Join meeting
              </span>
            )}
          </Button>
        </form>

        <div className="mt-6 pt-5 border-t border-border/70 text-[10px] text-muted-foreground uppercase tracking-[0.18em] text-center">
          Powered by Yanthra · MeetIQ
        </div>
      </div>
    </div>
  );
}

function RoomToolbar({ onLeave, displayName }) {
  const { localParticipant } = useLocalParticipant();
  const { toggle, enabled } = useTrackToggle({ source: Track.Source.Microphone });
  const isMicOn = !!enabled;
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card/80 backdrop-blur">
      <div className="flex items-center gap-3">
        <span className="relative inline-flex w-2.5 h-2.5">
          <span className="absolute inset-0 rounded-full bg-rose-500 yl-pulse-dot" />
          <span className="absolute inset-0 rounded-full bg-rose-500" />
        </span>
        <span className="eyebrow text-rose-700">Live</span>
        <span className="text-sm font-semibold text-foreground/80">
          {displayName} · joined as {localParticipant?.name || localParticipant?.identity}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          data-testid="room-mic-btn"
          onClick={() => toggle()}
          size="sm"
          className={`rounded-full h-10 px-4 ${isMicOn ? "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" : "bg-rose-500 text-white hover:bg-rose-600"}`}
        >
          {isMicOn ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
          {isMicOn ? "Mute" : "Unmute"}
        </Button>
        <Button
          data-testid="room-leave-btn"
          onClick={onLeave}
          variant="outline"
          size="sm"
          className="rounded-full h-10 px-4"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Leave
        </Button>
      </div>
    </div>
  );
}

function GuestLiveFeed({ meetingId, captionToken }) {
  const { captions, alert } = useLiveCaptions(meetingId, !!captionToken, 60, captionToken);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!captionToken) return null;
  return (
    <div data-testid="guest-live-feed">
      <div className="eyebrow mb-3">Live transcript &amp; quality</div>
      <LiveTranscription captions={captions} agendaAlert={alert} live elapsed={elapsed} />
    </div>
  );
}

function ParticipantsList() {
  const participants = useParticipants();
  return (
    <div data-testid="room-participants" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {(participants || []).map((p) => {
        const initials = (p.name || p.identity || "?")
          .split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
        return (
          <div
            key={p.identity}
            data-testid={`room-participant-${p.identity}`}
            className={`rounded-3xl border bg-card p-5 transition-shadow ${p.isSpeaking ? "border-primary shadow-lift" : "border-border shadow-card"}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full tone-lavender grid place-items-center font-heading text-lg ${p.isSpeaking ? "ring-4 ring-primary/40" : ""}`}>
                {initials}
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{p.name || p.identity}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  {p.isSpeaking ? "Speaking" : (p.isMicrophoneEnabled ? "Listening" : "Muted")}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MeetingJoin() {
  const { meetingId } = useParams();
  const [joinInfo, setJoinInfo] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState(null);
  const [left, setLeft] = useState(false);

  useEffect(() => {
    api.get(`/meetings/${meetingId}/join-info`)
      .then(({ data }) => setJoinInfo(data))
      .catch((e) => setError(formatApiError(e)));
  }, [meetingId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="rounded-3xl border border-border bg-card p-8 max-w-md text-center">
          <h1 className="font-heading text-2xl">Cannot join meeting</h1>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
          <Link to="/" data-testid="join-error-home-link" className="mt-5 inline-block text-sm font-semibold underline">Go home</Link>
        </div>
      </div>
    );
  }
  if (!joinInfo) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-sm text-muted-foreground">
        Loading meeting…
      </div>
    );
  }

  if (left) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="rounded-3xl border border-border bg-card p-8 max-w-md text-center">
          <h1 className="font-heading text-3xl">You have left the meeting.</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            The organizer will share the AI-generated minutes with you shortly.
          </p>
          <Link to="/" data-testid="join-left-home-link" className="mt-5 inline-block text-sm font-semibold underline">Go home</Link>
        </div>
      </div>
    );
  }

  if (!credentials) {
    return <Lobby meetingId={meetingId} joinInfo={joinInfo} onJoined={setCredentials} />;
  }

  return (
    <LiveKitRoom
      data-testid="livekit-room"
      serverUrl={credentials.ws_url}
      token={credentials.token}
      connect={true}
      audio={true}
      video={false}
      onDisconnected={() => setLeft(true)}
      data-lk-theme="default"
      style={{ minHeight: "100vh" }}
    >
      <div className="min-h-screen flex flex-col bg-background">
        <RoomToolbar onLeave={() => setLeft(true)} displayName={joinInfo.title} />
        <main className="flex-1 p-6 max-w-6xl mx-auto w-full">
          <div className="eyebrow mb-3">Participants in this room</div>
          <ParticipantsList />
          <div className="mt-8">
            <GuestLiveFeed meetingId={meetingId} captionToken={credentials.caption_token} />
          </div>
        </main>
        <RoomAudioRenderer />
      </div>
    </LiveKitRoom>
  );
}
